import React, { useCallback, useEffect, useRef, useState } from 'react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { Redirect, Route, useHistory } from 'react-router-dom';
import VirtualizedView, { RowGrid } from 'shared/components/VirtualizedView';
import { columnTypes, getRowHeight } from 'shared/components/tables';
import {
  removeConnections,
  useClient,
  useMutation,
  useQuery,
  useQueryRefresh,
} from 'shared/apollo';
import Grouping from './Grouping';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { TYPOGRAPHY } from 'shared/design-system/settings/variables/Typography';
import { ANIMATIONS } from 'shared/design-system/settings/variables/Animation';
import LinearProgress from '@mui/material/LinearProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import NavSelect from 'shared/components/NavSelect';
import { ConfirmationDialog } from 'shared/components';
import Program from '../Reports/Program';
import { QuickDrawer } from 'shared/components/QuickDrawer';
import { ReportBuilderInner } from '../ReportBuilder';
import Typography from '@mui/material/Typography';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import WizardHeader from '../ReportBuilder/Components/WizardHeader';
import camelCase from 'lodash/camelCase';
import get from 'lodash/get';
import { gql } from 'apollo-boost';
import moment from 'moment-timezone';
import styled from 'styled-components';
import useHash from 'shared/utils/useHash';
import { useUser } from 'shared/auth';
import {
  CalendarContainer,
  EventView,
  GroupsContainer,
  Page,
  Root,
} from './styled';
import { GroupTitle } from 'npo/pages/Reports/styled';
import { formattedDate } from 'shared/utils/date';
import Todo from './Todo';
import { TaskProvider } from './TaskProvider';

const checkOptions = [
  { value: 'all', label: 'All' },
  { value: 'my-task', label: 'My Tasks Only' },
  { value: 'include-complete', label: 'Include Completed Tasks' },
];

function TasksWrapper({
  match: {
    params: { group },
  },
}) {
  if (location.hostname !== 'localhost') {
    // eslint-disable-line
    const data = useMutation(setupTasksMutation); // eslint-disable-line

    if (!data) {
      return <LinearProgress variant="query" />;
    }
  }

  return <TasksMain {...{ group }} />;
}

function TasksMain({ group }) {
  const { data: query, refresh: onRefresh, isLoading } = useQueryRefresh(
    tasksQuery,
  );
  const { data: reportsData, refresh: onReportsRefresh } = useQueryRefresh(
    reportsQuery,
  );
  const {
    data: investmentsData,
    refresh: onRefreshInvestments,
  } = useQueryRefresh(investmentsQuery);

  const todoInvestments = investmentsData?.allInvestments
    ? removeConnections(investmentsData?.allInvestments || []).filter(
        inv => inv.status !== 'ALLOCATED',
      )
    : [];
  const usersData = useQuery(usersQuery);
  const user = useUser();
  const userId =
    user && parseInt(user.effectiveUser?.attributes['custom:user_id']);
  const [showMyTasks, setShowMyTasks] = useState();
  const [includeCompletedTasks, setIncludeCompletedTasks] = useState();
  const [filter, setFilter] = useState();
  const [wizardTab, setWizardTab] = useState('');
  const [cachedQuestions, setCachedQuestions] = useState([]);
  const [loadTabQuestion, setLoadTabQuestion] = useState();
  const [allocationAnswers, setAllocationAnswers] = useState({});
  const handleSearchInput = useCallback(event => {
    setFilter(event.target.value.toLowerCase());
  }, []);
  const data0 = query && removeConnections(query.allTasks);
  const [filteredDate, setFilteredDate] = useState();
  const filteredDateString = filteredDate && filteredDate.toDateString();

  const data01 =
    data0 &&
    data0.map(task => ({
      ...task,
      dueDate: fromET(task.dueDate),
      investment:
        get(task, 'invitation.investment') ||
        get(task, 'allocation.investment'),
    }));

  const data1 =
    data01 &&
    data01
      .filter(
        ({ allocation }) =>
          !showMyTasks ||
          get(allocation, 'report.program.managerId') === userId,
      )
      .filter(({ status }) => includeCompletedTasks || status !== 'COMPLETED')
      .filter(
        ({ investment }) => investment && investment.status !== 'ALLOCATED',
      );

  const data =
    data1 &&
    data1
      .filter(
        ({ dueDate }) =>
          !filteredDate || dueDate.toDateString() === filteredDateString,
      )
      .map(task => {
        const manager = get(task, 'allocation.report.program.manager');
        let programManagerName =
          manager &&
          `${manager.lastName}, ${manager.firstName} (${manager.email})`;
        let programName = '';
        if (!programManagerName) {
          const allocations = task?.invitation?.investment?.allocations;
          if (allocations && allocations.length > 0 && allocations[0]?.report) {
            const program = allocations[0]?.report?.program;
            programManagerName =
              program?.manager &&
              `${program?.manager.lastName}, ${program?.manager.firstName} (${program?.manager.email})`;
            programName = program?.name;
          }
        }
        programName = programName || task.allocation?.report?.program?.name;
        task.reportYear =
          parseInt(
            (get(task, 'allocation.report.endDate') || '').split('-')[0],
          ) || null;
        return {
          ...task,
          name: nameForTask(task),
          dueDateCategory: dateCategory(task.dueDate),
          dueDateText:
            task.dueDate &&
            `${task.dueDate.toLocaleDateString()} ${getShortTimeString(
              task.dueDate,
            )}`,
          programManagerName,
          programName,
        };
      })
      .filter(task => !filter || matchQuickFilter(task, filter))
      .sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());

  // adds investments not listed on task

  const serializeInvestmentAsTask = inv => {
    return {
      taskId: inv.dbId,
      isInvestment: true,
      taskType: 'ALLOCATE',
      status: 'UNCOMPLETED',
      dueDateCategory: 'noDate',
      name: `Allocate investment from ${inv?.portfolio?.funder?.name}`,
      invitation: {},
      dueDate: inv.dueDate,
      investment: {
        allocation: [],
        dbId: inv.dbId,
        status: inv.status,
        donation: inv.donation,
        portfolio: {
          funder: {
            name: inv?.portfolio?.funder?.name,
          },
        },
      },
    };
  };

  if (todoInvestments && todoInvestments.length > 0) {
    const listedInvestments = (data || []).map(task => task?.investment?.dbId);
    todoInvestments.forEach(inv => {
      if (listedInvestments.indexOf(inv.dbId) === -1) {
        const newTask = serializeInvestmentAsTask(inv);
        if (typeof data === 'object') {
          data.unshift(newTask);
        }
      }
    });
  }

  const programsAndYears = getProgramsAndYears(reportsData);
  const users =
    usersData &&
    removeConnections(usersData.allUsers).sort((a, b) =>
      a.lastName.localeCompare(b.lastName),
    );

  const groups = groupBy(data, groupByField(group));
  /*const events = data && data
    .map(({ name, dueDate }) => ({
      title: <a href="#">{name}</a>,
      tooltip: name,
      start: new Date(dueDate.getTime() - 60 * 60 * 1000),
      end: dueDate,
    }))*/

  const grBase = data01?.filter(
    ({ allocation }) =>
      !showMyTasks || get(allocation, 'report.program.managerId') === userId,
  );
  const grCompleted =
    grBase?.filter(val => val?.status?.toLowerCase() === 'completed') ?? [];
  const grOverdue =
    groups?.filter(gr => gr.key === 'overdue')?.[0]?.values ?? [];

  const groupsByStatus = [
    { key: 'overdue', values: grOverdue },
    { key: 'completed', values: grCompleted },
  ];

  const groupsDisplay = group === 'by-due-date' ? groupsByStatus : groups;

  const events =
    data1 &&
    groupBy(data1, 'dueDate').map(({ key: dueDate, values }) => ({
      title: values.length,
      tooltip: values.map(({ name }) => name).join('\n'),
      status: values.every(({ status }) => status === 'OVERDUE')
        ? 'overdue'
        : values.every(({ status }) => status === 'COMPLETED')
        ? 'completed'
        : values.every(({ status }) => status === 'UNCOMPLETED')
        ? 'uncompleted'
        : 'mixed',
      start: new Date(dueDate.getTime() - 60 * 60 * 1000),
      end: dueDate,
    }));
  const selectedEvent =
    events &&
    events.find(({ end }) => end.toDateString() === filteredDateString);
  if (group !== 'by-due-date' && groups) {
    groups.sort((a, b) =>
      a.key ? (b.key ? a.key.localeCompare(b.key) : 1) : b.key ? -1 : 0,
    );
  }

  const titleMapper = makeGroupTitleMapper(group);

  const [columns, setColumns] = useState();
  const hash = useHash() || '';
  const hashParts = hash.split('/').slice(1);
  const hashRoot = hashParts[0];
  const isCalendarVisible = hashRoot === 'calendar';
  const selectedTaskId = hashRoot === 'task' && parseInt(hashParts[1]);
  //const selectedTask = selectedTaskId && data01 && data01.find(({ taskId }) => taskId === selectedTaskId)
  const selectedTask =
    selectedTaskId &&
    data &&
    data.find(({ taskId }) => taskId === selectedTaskId);

  const [isProgramMenuVisible, setIsProgramMenuVisible] = useState();
  const [programMenuAnchorEl, setProgramMenuAnchorEl] = useState();
  const [selectedReportId, setSelectedReportId] = useState();
  const [selectedReportYear, setSelectedReportYear] = useState();
  const client = useClient();
  const [isChangeProgramInProgress, setIsChangeProgramInProgress] = useState();
  const handleChangeProgram = useCallback(
    async programId => {
      setIsChangeProgramInProgress(true);
      await client.mutate({
        mutation: changeProgramMutation,
        variables: {
          id: selectedReportId,
          programId,
        },
      });
      setIsProgramMenuVisible(false);
      setIsChangeProgramInProgress(false);
      onRefresh();
      onRefreshInvestments();
      onReportsRefresh && onReportsRefresh();
    },
    [
      client,
      onRefresh,
      onReportsRefresh,
      selectedReportId,
      onRefreshInvestments,
    ],
  );
  const [isManagerMenuVisible, setIsManagerMenuVisible] = useState();
  const [managerMenuAnchorEl, setManagerMenuAnchorEl] = useState();
  const [selectedProgramId, setSelectedProgramId] = useState();
  const [isChangeManagerInProgress, setIsChangeManagerInProgress] = useState();
  const handleChangeManager = useCallback(
    async managerId => {
      setIsChangeManagerInProgress(true);
      await client.mutate({
        mutation: changeManagerMutation,
        variables: {
          id: selectedProgramId,
          managerId,
        },
      });
      setIsManagerMenuVisible(false);
      setIsChangeManagerInProgress(false);
      onRefresh();
      onRefreshInvestments();
    },
    [client, onRefresh, selectedProgramId, onRefreshInvestments],
  );
  const handleProgramMenuClose = useCallback(() => {
    setIsProgramMenuVisible(false);
  }, []);
  const handleManagerMenuClose = useCallback(() => {
    setIsManagerMenuVisible(false);
  }, []);

  useEffect(() => {
    setColumns(
      [
        {
          headerName: '',
          field: 'status',
          width: 8,
          valueFormatter: ({ value }) =>
            value === 'COMPLETED' ? '✔️' : '\xa0',
          filter: false,
          tooltipValueGetter: ({ value }) => value,
          //        cellRenderer: renderColumnWithToolip,
        },
        {
          headerName: 'Program',
          field: 'allocation.report.program.name',
          valueFormatter: programNameFormatter,
          flex: 2,
          //        tooltipValueGetter: ({ value, data }) => `${programNameFormatter({ value, data })}\n${completionTooltip(data)}`,
          cellRenderer: renderProgramColumn,
          onCellClicked: ({ event, data }) => {
            if (data.allocation) {
              setProgramMenuAnchorEl(event.target);
              setSelectedReportYear(data.reportYear);
              setSelectedReportId(data.allocation.reportId);
              setIsProgramMenuVisible(true);
            } else {
              alert('This task needs to be allocated first');
            }
          },
        },
        { headerName: 'Year', field: 'reportYear', width: 60 },
        {
          headerName: 'PM',
          field: 'programManagerName',
          valueFormatter: valueFormatter,
          flex: 1,
          cellRenderer: renderPMColumn,
          //        tooltipValueGetter: valueFormatter,
          onCellClicked: ({ event, data }) => {
            if (data.allocation) {
              if (!data.allocation.report.program) {
                alert('You must assign a program first');
              } else {
                setManagerMenuAnchorEl(event.target);
                setSelectedProgramId(data.allocation.report.programId);
                setIsManagerMenuVisible(true);
              }
            } else {
              alert('This task needs to be allocated first');
            }
          },
        },
        {
          headerName: 'Donor',
          field: 'investment.portfolio.funder.name',
          flex: 2,
          cellRenderer: renderColumnWithToolip,
        },
        {
          headerName: 'Due Date',
          field: 'dueDateText',
          valueFormatter: ({ value, data }) => {
            const date = value && value.split(' ')[0];
            const dueDate = data.dueDate;
            return dueDate || date
              ? formattedDate(dueDate || date)
              : 'No date set';
          },
          width: 125,
        },
        {
          headerName: 'Tasks',
          field: 'name',
          flex: 3,
          cellRenderer: renderTasksColumn,
        },
      ]
        .filter((_, index) => index !== columnIndexForGroup(group))
        .filter(
          ({ headerName }) =>
            !isCalendarVisible || !['PM', ''].includes(headerName),
        )
        .map(col => ({ ...col, filter: false, sortable: true })),
    );
  }, [group, isCalendarVisible]);

  useEffect(() => {
    if (window.location.search) {
      const includeMyTask = window.location.search.includes('my-task');
      const includeCompleted = window.location.search.includes(
        'include-complete',
      );
      setShowMyTasks(includeMyTask);
      setIncludeCompletedTasks(includeCompleted);
    }
  }, []);

  const handleCalendarNavigate = useCallback(
    date => {
      const dateString = date.toDateString();
      const event = events.find(({ end }) => end.toDateString() === dateString);
      if (event) {
        setFilteredDate(date);
      }
    },
    [events],
  );
  const handleSelectEvent = useCallback(({ end }) => {
    const onlyDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    setFilteredDate(onlyDate);
  }, []);
  const handleFilteredDateClear = useCallback(() => {
    setFilteredDate(null);
  }, []);
  const handleProgramClose = useCallback(() => {
    location.hash = ''; // eslint-disable-line
  }, []);
  const handleRenderRow = useCallback(
    ({
      value,
      columns,
      style, // Style object to be applied to row (to position it)
      selectedRows,
      setSelectedRows,
      hasCheckBox,
      isLastRow,
      index,
    }) => {
      return (
        <RowGrid
          {...{
            style,
            group,
            value,
            columns,
            columnTypes,
            selectedRows,
            setSelectedRows,
            hasCheckBox,
            isLastRow,
            index,
          }}
          groupKeyField={groupByField(group)}
        />
      );
    },
    [group],
  );
  const handleRenderGroupHeader = useCallback(
    ({
      style,
      value,
      isScrolling,
      isVisible,
      isExpanded,
      selectedRows,
      setSelectedRows,
      onExpandCollapse,
    }) => {
      const total = value.values?.length;
      const mergingStyles = { ...style, cursor: 'pointer' };
      const rawTitle = titleMapper(value.groupHeader);
      let title = rawTitle;
      if (!title) {
        title = 'Completed';
      }
      return (
        <GroupHeaderContainer
          onClick={onExpandCollapse}
          style={mergingStyles}
          isExpanded={isExpanded}
        >
          <GroupTitle>
            {title}{' '}
            <span>
              ({total} {total === 1 ? 'task' : 'tasks'})
            </span>
          </GroupTitle>
        </GroupHeaderContainer>
      );
      /*      <GroupHeader
            groupKey={value.groupHeader}
            values={value.values}
            title={}
            itemAmount={value.values.length}
            {...{
              style, isScrolling, isVisible, group,
              selectedRows, setSelectedRows, onExpandCollapse
            }}
            isExpanded={value.isExpanded}
          />*/
    },
    [titleMapper],
  );
  const virtualized = useRef();
  const [expanded, setExpanded] = useState(false);
  const handleExpand = useCallback(() => {
    if (expanded) {
      virtualized.current.collapseAll();
    } else {
      virtualized.current.expandAll();
    }
    setExpanded(!expanded);
  }, [expanded]);
  const { push } = useHistory();
  const handleCloseAllocationWizard = useCallback(() => {
    push('#');
    window.location.hash = '/';
  }, [push]);

  /* handles when new question is presented */
  const handleQuestionChange = useCallback(
    question => {
      setWizardTab(question?.tab || 'program');
      setCachedQuestions([...cachedQuestions, question]);
    },
    [cachedQuestions],
  );

  const handleOnNewAnswer = useCallback(newAnswer => {
    setAllocationAnswers(newAnswer);
  }, []);

  const onPressTab = useCallback(question => {
    setLoadTabQuestion(question);
  }, []);

  const handleOnCompleteSection = useCallback(
    question => {
      onRefresh();
      onRefreshInvestments();
    },
    [onRefresh, onRefreshInvestments],
  );

  return (
    <>
      <>
        <TaskProvider>
          <Todo allReports={getAllReports(programsAndYears)} />
        </TaskProvider>
        <ConfirmationDialog
          openDialog={selectedTask && !selectedTask?.investment?.dbId}
          onCloseDialog={handleCloseAllocationWizard}
          actionButtonOne={handleCloseAllocationWizard}
          textButtonOne="Ok"
          title="The selected task seems to be incorrect and can't be allocated."
          header="Contribution is required"
        />
        <QuickDrawer
          width="480px"
          title="Allocations"
          open={
            selectedTask &&
            selectedTask?.taskType !== 'CREATE' &&
            selectedTask?.investment?.dbId
          }
          onClose={handleCloseAllocationWizard}
        >
          <DrawerContainer>
            <WizardHeader
              donor={selectedTask?.investment?.portfolio?.funder?.name}
              contribution={selectedTask?.investment?.donation}
              year={moment(selectedTask?.dueDate).format('YYYY')}
              designated_program={
                selectedTask?.investment?.allocations?.[0]?.report?.program
                  ?.name
              }
              tab={wizardTab}
              cachedQuestions={cachedQuestions}
              onPressTab={onPressTab}
              allocationAnswers={allocationAnswers}
            />
            <ReportBuilderInner
              investmentId={selectedTask?.investment?.dbId}
              donationTotal={selectedTask?.investment?.donation}
              accountName={
                selectedTask?.investment?.portfolio?.funder?.name || ''
              }
              programName={selectedTask?.programName}
              sectionName="allocation"
              onQuestionChange={handleQuestionChange}
              onNewAnswer={handleOnNewAnswer}
              loadTabQuestion={loadTabQuestion}
              closeAllocationPopup={handleCloseAllocationWizard}
              onCompleteSection={handleOnCompleteSection}
            />
          </DrawerContainer>
        </QuickDrawer>
        {isCalendarVisible && events && (
          <CalendarContainer>
            <Calendar
              {...{ localizer, events }}
              views={calendarViews}
              drilldownView="agenda"
              tooltipAccessor="tooltip"
              components={calendarComponents}
              date={filteredDate}
              onNavigate={handleCalendarNavigate}
              selected={selectedEvent}
              onSelectEvent={handleSelectEvent}
            />
          </CalendarContainer>
        )}
        <Menu
          open={isProgramMenuVisible}
          getContentAnchorEl={null}
          anchorEl={programMenuAnchorEl}
          onClose={handleProgramMenuClose}
          {...{ transformOrigin, anchorOrigin }}
        >
          <MenuItem disabled>Select Program for Report:</MenuItem>
          {programsAndYears &&
            programsAndYears.map(({ key, values }) => (
              <MenuItem
                key={key}
                disabled={
                  isChangeProgramInProgress ||
                  values.some(({ year }) => year === selectedReportYear)
                }
                onClick={() => handleChangeProgram(key)}
              >
                {values[0].program.name}
              </MenuItem>
            ))}
          {(!programsAndYears || isChangeProgramInProgress) && (
            <LinearProgress variant="query" />
          )}
        </Menu>
        <Menu
          open={isManagerMenuVisible}
          getContentAnchorEl={null}
          anchorEl={managerMenuAnchorEl}
          onClose={handleManagerMenuClose}
          {...{ transformOrigin, anchorOrigin }}
        >
          <MenuItem disabled>Select Manager for Program:</MenuItem>
          {users &&
            users.map(({ userId, firstName, lastName, email }) => (
              <MenuItem
                key={userId}
                onClick={() => handleChangeManager(userId)}
                disabled={isChangeManagerInProgress}
              >
                {lastName}, {firstName} ({email})
              </MenuItem>
            ))}
          {(!users || isChangeManagerInProgress) && (
            <LinearProgress variant="query" />
          )}
        </Menu>
      </>
    </>
  );
}

const getProgramsAndYears = reportsData => {
  return (
    reportsData &&
    groupBy(
      removeConnections(reportsData.allReports)
        .filter(({ programId }) => programId)
        .map(({ endDate, ...other }) => ({
          year: endDate && parseInt(endDate.split('-')[0]),
          ...other,
        })),
      'programId',
    )
  );
};

const getAllReports = programsAndYears => {
  return (
    programsAndYears?.reduce((acc, { values }) => {
      values.forEach(a => {
        if (a.programId === null) {
          return;
        }

        if (!acc[a.programId]) {
          acc[a.programId] = {};
        }

        acc[a.programId][a.year] = a;
      });

      return acc;
    }, {}) || {}
  );
};

export const parseProgramsAndYears = reportsData => {
  return getAllReports(getProgramsAndYears(reportsData));
};

export default function Tasks() {
  return (
    <>
      <Route path="/npo/todo/:group" component={TasksWrapper} />
      <Route exact path="/npo/todo">
        <Redirect to="/npo/todo/by-due-date" />
      </Route>
    </>
  );
}

const tasksQuery = gql`
  query {
    allTasks {
      edges {
        node {
          taskId
          taskType
          dueDate
          status
          completionDate
          completionUser {
            firstName
            lastName
          }
          allocation {
            reportId
            report {
              endDate
              programId
              program {
                id
                name
                managerId
                manager {
                  firstName
                  lastName
                  email
                }
              }
            }
            investment {
              dbId
              donation
              programNameByDonor
              portfolio {
                year
                funder {
                  name
                }
              }
            }
          }
          invitation {
            firstName
            lastName
            email
            investment {
              dbId
              donation
              status
              programNameByDonor
              allocations {
                edges {
                  node {
                    reportId
                    report {
                      endDate
                      programId
                      program {
                        name
                        managerId
                        manager {
                          firstName
                          lastName
                          email
                        }
                      }
                    }
                  }
                }
              }
              portfolio {
                year
                funder {
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`;

const setupTasksMutation = gql`
  mutation {
    setupTasks {
      success
    }
  }
`;

const hourMs = 60 * 60 * 1000;
const dateOnly = date => new Date(date.toDateString());
function dateCategory(date) {
  const today = new Date();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();
  const todayDay = today.getDate();
  const weekStart = dateOnly(
    new Date(today.getTime() - today.getDay() * 24 * hourMs),
  );
  const weekEnd = new Date(weekStart.getTime() + 7 * 24 * hourMs);
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  if (date < today) {
    return 'overdue';
  }

  if (year === todayYear) {
    if (month === todayMonth) {
      if (day === todayDay) {
        return 'today';
      } else {
        if (date < weekEnd) {
          return 'week';
        }
        return 'month';
      }
    }
  }

  if (date < weekEnd) {
    return 'week';
  }
  return 'later';
}
const groupByFieldMap = {
  byDueDate: 'dueDateCategory',
  byDonor: 'investment.portfolio.funder.name',
  byManager: 'programManagerName',
  byProgram: 'allocation.report.program.name',
};
const groupByField = slug => groupByFieldMap[camelCase(slug)];

const columnForGroupMap = {
  byDonor: 4,
  byManager: 3,
  byProgram: 1,
};
const columnIndexForGroup = slug => columnForGroupMap[camelCase(slug)];

function groupBy(array, field) {
  if (!array) {
    return array;
  }

  const result = [];
  const cache = {};

  for (const item of array) {
    const key = get(item, field);
    let value = cache[key];
    if (!value) {
      value = cache[key] = { key, values: [] };
      result.push(value);
    }

    value.values.push(item);
  }

  return result;
}

const byDueDateTitles = {
  overdue: 'Overdue',
  noDate: 'No Date Set',
  today: 'Today',
  week: 'This Week',
  month: 'This Month',
  later: 'Later',
};
const titleMappers = {
  byDueDate: value => byDueDateTitles[value],
  byDonor: value => value || '(no donor)',
  byManager: value => value || '(no manager)',
  byProgram: value => value || '(no program)',
};
const makeGroupTitleMapper = grouping => value =>
  titleMappers[camelCase(grouping)](value);

const hoursLookup = [
  '12AM',
  '1AM',
  '2AM',
  '3AM',
  '4AM',
  '5AM',
  '6AM',
  '7AM',
  '8AM',
  '9AM',
  '10AM',
  '11AM',
  '12PM',
  '1PM',
  '2PM',
  '3PM',
  '4PM',
  '5PM',
  '6PM',
  '7PM',
  '8PM',
  '9PM',
  '10PM',
  '11PM',
];

const getShortTimeString = date => hoursLookup[date.getHours()];

const quickFilterFields = [
  'allocation.report.program.name',
  'reportYear',
  'programManagerName',
  'investment.portfolio.funder.name',
  'dueDateText',
  'dueDateCategory',
  'name',
];

function matchQuickFilter(obj, filter) {
  filter = filter.toLowerCase();
  return quickFilterFields.some(field =>
    String(get(obj, field))
      .toLowerCase()
      .includes(filter),
  );
}

const colorByStatus = {
  // eslint-disable-line
  OVERDUE: 'rgba(255,0,0,.1)',
  COMPLETED: 'rgba(0,255,0,.1)',
  UNCOMPLETED: 'transparent',
};

const nameForReport = ({ allocation, reportYear }) =>
  allocation
    ? `: ${get(allocation, 'report.program.name')} (${reportYear})`
    : '';

function nameForTask({ taskType, investment, allocation, reportYear }) {
  switch (
    taskType // eslint-disable-line
  ) {
    case 'ALLOCATE':
      return `Allocate investment from ${get(
        investment,
        'portfolio.funder.name',
      )}`;
    case 'CREATE':
      return 'Create first program';
    case 'SUBMIT':
      return 'Submit for feedback' + nameForReport({ allocation, reportYear });
    case 'RESOLVE':
      return 'Resolve feedback' + nameForReport({ allocation, reportYear });
    case 'PUBLISH':
      return 'Publish report' + nameForReport({ allocation, reportYear });
    case 'UPDATE':
      return (
        'Interim report update' + nameForReport({ allocation, reportYear })
      );
    case 'FINALIZE':
      return 'Finalize report' + nameForReport({ allocation, reportYear });
  }
  return 'error';
}

const fromET = date => moment.tz(date, 'US/Eastern').toDate();
const renderTasksColumn = ({ data, value }) => {
  return value
    ? `<a href="#/task/${data.taskId}" title="${value}">${value}</a>`
    : null;
};
const completionTooltip = ({ completionDate, completionUser }) =>
  completionDate
    ? `&#013;Completed on ${completionDate} by ${completionUser.firstName} ${completionUser.lastName}`
    : '';
const renderColumnWithToolip = ({ value, data }) =>
  `<span title="${value}${completionTooltip(data)}">${value || ''}</span>`;
const renderProgramColumn = ({ value, data }) =>
  renderColumnWithToolip({
    value: `${programNameFormatter({ value, data })}`,
    data,
  });
const renderPMColumn = ({ value, data }) => {
  return renderColumnWithToolip({
    value: value || '(unallocated)',
    data,
  });
};

const calendarViews = ['month'];

// Setup the localizer by providing the moment (or globalize) Object
// to the correct localizer.
const localizer = momentLocalizer(moment); // or globalizeLocalizer

const programNameFormatter = ({ data, value }) => {
  return (
    value ||
    data.programName ||
    data.invitation?.investment?.programNameByDonor ||
    (data.allocation ? '(no program)' : '(unallocated)')
  );
};

const valueFormatter = ({ data, value }) =>
  data.allocation ? value : '(unallocated)';

const Event = ({ event }) => (
  <EventView status={event.status}>{event.title}</EventView>
);

const calendarComponents = {
  event: Event,
};

const anchorOrigin = {
  vertical: 'bottom',
  horizontal: 'center',
};
const transformOrigin = {
  vertical: 'top',
  horizontal: 'center',
};

export const reportsQuery = gql`
  query {
    allReports {
      edges {
        node {
          dbId
          year
          endDate
          programId
          program {
            name
          }
        }
      }
    }
  }
`;

const investmentsQuery = gql`
  query {
    allInvestments(filters: { enabled: true }) {
      edges {
        node {
          dbId
          status
          donation
          programNameByDonor
          portfolio {
            funder {
              name
            }
          }
        }
      }
    }
  }
`;

const changeProgramMutation = gql`
  mutation changeProgram($id: Int!, $programId: Int!) {
    updateReport(id: $id, programId: $programId) {
      success
    }
  }
`;

const usersQuery = gql`
  query {
    allUsers {
      edges {
        node {
          userId
          firstName
          lastName
          email
        }
      }
    }
  }
`;

const changeManagerMutation = gql`
  mutation changeManager($id: Int!, $managerId: Int!) {
    updateProgram(id: $id, managerId: $managerId) {
      success
    }
  }
`;

const GroupHeaderContainer = styled.div`
  padding: 4px;
  border: 1px solid #cbd6e2;
  ${({ isExpanded }) =>
    isExpanded
      ? `border-top-left-radius: 4px;
      border-top-right-radius: 4px;
      padding: 8px 16px 0 16px;`
      : `border-radius: 4px;
      padding: 8px 16px;;
      height: 64px !important;`}
  width: calc(100% - 40px) !important;
  display: flex;
  align-items: center;
  background-color: #fff;
`;

const ExpandAll = styled.div`
  color: #0072a2;
  font-size: 16px;
  font-weight: regular;
  cursor: pointer;
  text-decoration: underline;
  svg {
    transform: translateY(7px);
  }
`;

const ExpandContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 5px 0;
`;

const DrawerContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  .builder-root {
    background-color: purple;
    min-height: inherit;
    flex: 1;
    display: flex;
  }
  // Allocation Wizard specific style for the Report Builder components rendered in QuickDrawer
  .builder-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    .MuiSvgIcon-root {
      right: -30px !important;
    }
    .question-info {
      margin-bottom: ${SPACERS.tiny};
    }
    .question-title {
      font-size: ${TYPOGRAPHY.headingSubheading};
      margin-bottom: ${SPACERS.small};
    }
    .bottom-bar {
      width: 100%;
      position: relative;
      left: 0;
      top: 0;
    }
  }
`;
